/* eslint-disable react-hooks/exhaustive-deps */
import {
  BusinessGoalDateFilter,
  BusinessGoalPriority,
  UserTask,
  UserTaskItemStatus,
  UserTaskQuickFilter,
  UserTasksFilterInput,
  useGetAdvisorAssignedTasksQuery,
  useGetUserTasksQuery,
} from '__generated__/api-types-and-hooks'
import { ITaskSummaryItem } from 'components/AppDashboard/interfaces'
import { useEffect, useState, useMemo } from 'react'
import { getTenantId } from 'utils/getTenantId'
import moment from 'moment'

interface IUseGetUserTasksInput {
  filter?: BusinessGoalDateFilter
  userId?: string
  quickFilter?: UserTaskQuickFilter
  assigneeId?: string
}

export const useGetAdvisorAssignedTasks = (input?: IUseGetUserTasksInput) => {
  const tenantId = getTenantId()
  const [userTasks, setUserTasks] = useState<UserTask[]>([])
  const filter = { dateRange: input?.filter } as UserTasksFilterInput
  if (input?.quickFilter) {
    filter.quickFilter = input.quickFilter
  }
  if (input?.assigneeId) {
    filter.assigneeId = [input.assigneeId]
  }
  const {
    data: advisorData,
    isLoading: isAdvisorTasksLoading,
    refetch: refetchAdvisorTasks,
  } = useGetAdvisorAssignedTasksQuery(
    {
      input: { tenantId, filter },
    },
    { refetchOnWindowFocus: false }
  )

  const {
    data: otherTasks,
    isLoading: isOtherTasksLoading,
    refetch: refetchOtherTasks,
  } = useGetUserTasksQuery(
    {
      input: {
        tenantId,
        filter: {
          quickFilter: UserTaskQuickFilter.NoProjectAssigned,
          dateRange: input?.filter,
          ...(input?.assigneeId ? { assigneeId: [input.assigneeId] } : {}),
        },
      },
    },
    { refetchOnWindowFocus: false }
  )

  useEffect(() => {
    if (
      advisorData &&
      advisorData.getAdvisorAssignedTasks &&
      otherTasks &&
      otherTasks.getUserTasks
    ) {
      // Filter out deleted tasks
      const allTasks = [
        ...advisorData.getAdvisorAssignedTasks,
        ...otherTasks.getUserTasks,
      ] as UserTask[]
      const activeTasks = allTasks.filter((task) => task.status !== UserTaskItemStatus.Deleted)

      // Apply additional date filtering on frontend as a safety measure
      // This ensures tasks with future dates don't slip through
      let filteredTasks = activeTasks
      if (input?.filter === BusinessGoalDateFilter.Today) {
        const today = moment().format('YYYY-MM-DD')
        filteredTasks = activeTasks.filter((task) => {
          // Exclude DONE tasks
          if (task.status === UserTaskItemStatus.Done) {
            return false
          }

          // Only include tasks with valid statuses: TODO, IN_PROGRESS, or BLOCKED
          const validStatuses = [
            UserTaskItemStatus.Todo,
            UserTaskItemStatus.InProgress,
            UserTaskItemStatus.Blocked,
          ]
          if (!task.status || !validStatuses.includes(task.status)) {
            return false
          }

          // Check if we have required date fields
          if (!task.expectedEndDate) {
            return false
          }

          const startDate = task.startDate ? moment(task.startDate) : null
          const endDate = moment(task.expectedEndDate)
          const todayMoment = moment(today)

          // Include tasks where:
          // 1. Start date is today or earlier, OR
          // 2. End date is today, OR
          // 3. Past due (end date has passed)
          // Note: If startDate is missing, we only check endDate conditions
          return (
            (startDate && startDate.isSameOrBefore(todayMoment, 'day')) ||
            endDate.isSame(todayMoment, 'day') ||
            endDate.isBefore(todayMoment, 'day')
          )
        })
      }

      setUserTasks(filteredTasks)
    } else if (advisorData && advisorData.getAdvisorAssignedTasks) {
      const activeTasks = (advisorData.getAdvisorAssignedTasks as UserTask[]).filter(
        (task) => task.status !== UserTaskItemStatus.Deleted
      )
      setUserTasks(activeTasks)
    } else if (otherTasks && otherTasks.getUserTasks) {
      const activeTasks = (otherTasks.getUserTasks as UserTask[]).filter(
        (task) => task.status !== UserTaskItemStatus.Deleted
      )
      setUserTasks(activeTasks)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advisorData, otherTasks, input?.filter])

  const filterTaskByStatus = (tasksToFilter: UserTask[], status: UserTaskItemStatus) => {
    const tasks = tasksToFilter.filter((task) => task.status === status)
    return tasks.map((task) => ({
      ...task,
      priorityLabel: task.taskPriority?.split('_').join(' ').toLowerCase() ?? '',
      priorityStatus: task.taskPriority ?? BusinessGoalPriority.MediumPriority,
    }))
  }

  const todoTasks: ITaskSummaryItem[] = useMemo(
    () => filterTaskByStatus(userTasks, UserTaskItemStatus.Todo),
    [userTasks]
  )
  const inProgressTasks: ITaskSummaryItem[] = useMemo(
    () => filterTaskByStatus(userTasks, UserTaskItemStatus.InProgress),
    [userTasks]
  )
  const blockedTasks: ITaskSummaryItem[] = useMemo(
    () => filterTaskByStatus(userTasks, UserTaskItemStatus.Blocked),
    [userTasks]
  )
  const completedTasks: ITaskSummaryItem[] = useMemo(
    () => filterTaskByStatus(userTasks, UserTaskItemStatus.Done),
    [userTasks]
  )
  const uncompletedTasks: ITaskSummaryItem[] = useMemo(() => {
    const tasks = userTasks.filter((task) => task.status !== UserTaskItemStatus.Done)
    return tasks.map((task) => ({
      ...task,
      priorityLabel: task.taskPriority?.split('_').join(' ').toLowerCase() ?? '',
      priorityStatus: task.taskPriority!,
    }))
  }, [userTasks])

  return {
    userTasks,
    todoTasks,
    inProgressTasks,
    blockedTasks,
    completedTasks,
    uncompletedTasks,
    isUserTasksLoading: isAdvisorTasksLoading && isOtherTasksLoading,
    refetchUserTasks: () => {
      refetchAdvisorTasks()
      refetchOtherTasks()
    },
  }
}
